import { useState, useRef, useEffect, KeyboardEvent, ChangeEvent } from 'react';
import { Button, Input, Spinner } from 'reactstrap';
import useChat from '../../hooks/useChat';

interface Props {
  propertyCode: string;
  marketplaceType: string;
}

const FloatingChat = ({ propertyCode, marketplaceType }: Props) => {
  const [isOpen, setIsOpen] = useState(false);
  const [inputText, setInputText] = useState('');
  const {
    messages,
    isLoading,
    isInitialized,
    sendMessage,
    initChat,
    clearMessages,
  } = useChat();
  const messagesEndRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (isOpen && !isInitialized && propertyCode) {
      initChat(propertyCode, marketplaceType);
    }
  }, [isOpen, isInitialized, propertyCode, marketplaceType, initChat]);

  useEffect(() => {
    if (isOpen) {
      messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
    }
  }, [messages, isOpen]);

  const handleSend = async () => {
    if (!inputText.trim() || isLoading) return;
    const text = inputText;
    setInputText('');
    await sendMessage(text);
  };

  const handleKeyDown = (e: KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  };

  return (
    <div style={{ position: 'fixed', bottom: 24, right: 24, zIndex: 1050 }}>
      {isOpen && (
        <div
          style={{
            width: 408,
            height: 552,
            display: 'flex',
            flexDirection: 'column',
            borderRadius: 12,
            boxShadow: '0 10px 40px rgba(0,0,0,0.25)',
            overflow: 'hidden',
            background: '#fff',
            marginBottom: 12,
          }}
        >
          <div
            style={{
              background: 'linear-gradient(87deg, #172b4d 0%, #1a174d 100%)',
              padding: '12px 16px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
            }}
          >
            <div className="d-flex align-items-center">
              <i className="ni ni-chat-round text-white mr-2" />
              <span
                className="text-white font-weight-bold"
                style={{ fontSize: 14 }}
              >
                Asistente Propi
              </span>
            </div>
            <div className="d-flex align-items-center">
              <button
                onClick={clearMessages}
                title="Nueva conversación"
                style={{
                  background: 'none',
                  border: 'none',
                  cursor: 'pointer',
                  padding: '0 8px',
                }}
              >
                <i
                  className="ni ni-refresh-02 text-white"
                  style={{ fontSize: 12 }}
                />
              </button>
              <button
                onClick={() => setIsOpen(false)}
                style={{
                  background: 'none',
                  border: 'none',
                  cursor: 'pointer',
                  padding: 0,
                }}
              >
                <i className="ni ni-fat-remove text-white" />
              </button>
            </div>
          </div>

          <div
            style={{
              flex: 1,
              overflowY: 'auto',
              padding: '12px 16px',
              display: 'flex',
              flexDirection: 'column',
              gap: 10,
              background: '#f6f9fc',
            }}
          >
            {messages.length === 0 && !isLoading && (
              <div
                className="d-flex flex-column align-items-center justify-content-center h-100 text-muted"
                style={{ fontSize: 13 }}
              >
                <i
                  className="ni ni-chat-round mb-2"
                  style={{ fontSize: 32, opacity: 0.3 }}
                />
                ¿En qué puedo ayudarte?
              </div>
            )}
            {messages.map((msg) => (
              <div
                key={msg.id}
                style={{
                  display: 'flex',
                  justifyContent:
                    msg.role === 'user' ? 'flex-end' : 'flex-start',
                }}
              >
                <div
                  style={{
                    maxWidth: '80%',
                    padding: '8px 12px',
                    borderRadius:
                      msg.role === 'user'
                        ? '12px 12px 2px 12px'
                        : '12px 12px 12px 2px',
                    background: msg.role === 'user' ? '#5e72e4' : '#fff',
                    color: msg.role === 'user' ? '#fff' : '#32325d',
                    fontSize: 13,
                    lineHeight: 1.5,
                    boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
                    whiteSpace: 'pre-wrap',
                    wordBreak: 'break-word',
                  }}
                >
                  {msg.text}
                </div>
              </div>
            ))}
            {isLoading && (
              <div style={{ display: 'flex', justifyContent: 'flex-start' }}>
                <div
                  style={{
                    padding: '8px 12px',
                    borderRadius: '12px 12px 12px 2px',
                    background: '#fff',
                    boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
                  }}
                >
                  <Spinner size="sm" color="primary" />
                </div>
              </div>
            )}
            <div ref={messagesEndRef} />
          </div>

          <div
            style={{
              padding: '10px 12px',
              borderTop: '1px solid #e9ecef',
              display: 'flex',
              gap: 8,
              background: '#fff',
              alignItems: 'center',
            }}
          >
            <Input
              type="text"
              placeholder={
                isInitialized ? 'Escribe un mensaje...' : 'Iniciando...'
              }
              value={inputText}
              onChange={(e: ChangeEvent<HTMLInputElement>) =>
                setInputText(e.target.value)
              }
              onKeyDown={handleKeyDown}
              disabled={isLoading || !isInitialized}
              style={{ fontSize: 13, borderRadius: 20 }}
            />
            <Button
              color="primary"
              size="sm"
              onClick={handleSend}
              disabled={isLoading || !isInitialized || !inputText.trim()}
              style={{
                borderRadius: '50%',
                width: 36,
                height: 36,
                padding: 0,
                flexShrink: 0,
              }}
            >
              <i className="ni ni-send" style={{ fontSize: 14 }} />
            </Button>
          </div>
        </div>
      )}

      <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
        <button
          onClick={() => setIsOpen((o) => !o)}
          style={{
            width: 54,
            height: 54,
            borderRadius: '50%',
            background: 'linear-gradient(87deg, #5e72e4 0%, #825ee4 100%)',
            border: 'none',
            cursor: 'pointer',
            boxShadow: '0 4px 20px rgba(94,114,228,0.5)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
          }}
        >
          <i
            className={isOpen ? 'ni ni-fat-remove' : 'ni ni-chat-round'}
            style={{ color: '#fff', fontSize: 22 }}
          />
        </button>
      </div>
    </div>
  );
};

export default FloatingChat;
